import {TrackingServiceCard} from "./tracking_service_card";
import Slide from "./slide";
import {CinemaChangeEvent, CinemaInitializedEvent, TrackingFeatureLabels, PromoType, TrackingActionType} from "../types";

export default class ShopPromoLargeCard {
    private readonly trackingService: TrackingServiceCard;
    readonly slides: Array<Slide>
    private readonly promoType: PromoType;
    private readonly featureIndex: number;
    private readonly featureOrder: number;
    private readonly featureLabels: TrackingFeatureLabels;
    private readonly masFeatureTrackingLabels: TrackingFeatureLabels;

    constructor(shopPromoLargeCardDom: HTMLDivElement) {
        this.promoType = this.getPromoType(shopPromoLargeCardDom);
        this.featureIndex = this.getFeatureIndex(shopPromoLargeCardDom);
        this.featureOrder = this.getFeatureOrder(shopPromoLargeCardDom);
        this.featureLabels = this.getFeatureLabels(shopPromoLargeCardDom);
        this.masFeatureTrackingLabels = this.getMasFeatureTrackingLabels(shopPromoLargeCardDom);
        this.trackingService = new TrackingServiceCard()

        const slideSelector = this.isCinemaEnabled(shopPromoLargeCardDom) ? '.pl_cinema__slide' : '.promo_shoppromo-single-slide'
        const slideContainers = Array.from(shopPromoLargeCardDom.querySelectorAll(slideSelector));
        this.slides = slideContainers.map((slideDom, i) => new Slide(slideDom, i))
        this.slides.forEach((slide) => {
            slide.registerClickEventHandler(() => this.trackClick(slide, this.slides.length))
            if (slide.hasLever) {
                slide.registerLeverClickEventListener((e: Event) => this.openAndTrackLeverLargeShoppromo(e, slide, this.slides.length))
            }
        })

        if (this.isCinemaEnabled(shopPromoLargeCardDom)) {
            window.o_global.eventQBus.on("assets.cinema.initialized", (e) => this.trackInitialViewLargeShopPromo(e));
            window.o_global.eventQBus.on("assets.cinema.change", (e) => this.trackSlideLargeShopPromo(e));

            window.o_global.eventQBus.emit(
                "assets.cinema.init",
                `.promo_shoppromo-large-card--container[data-feature-index="${this.featureIndex}"].js_pl_cinema`
            )
        } else {
            const fakeCinemaInitializedEvent: CinemaInitializedEvent = {
                element: shopPromoLargeCardDom,
                currentSlides: [0]
            }
            this.trackInitialViewLargeShopPromo(fakeCinemaInitializedEvent)
        }
    }

    trackInitialViewLargeShopPromo(e: CinemaInitializedEvent) {
        const eventFeatureIndex = this.getFeatureIndex(e.element)
        if (e.element.className.includes("promo_shoppromo-large-card--container") && eventFeatureIndex == this.featureIndex && !e.element.getAttribute("data-tracked")) {
            if (this.isCinemaEnabled(e.element)) {
                const visibleSlides = this.slides.slice(e.currentSlides.at(0), e.currentSlides.at(-1)! + 1)
                this.trackingService.sendLargeShopPromoInitialViewTrackingEvent(this.promoType, this.featureOrder, this.featureIndex, this.slides.length, visibleSlides, this.featureLabels, this.masFeatureTrackingLabels);
            } else {
                this.trackingService.sendLargeShopPromoInitialViewTrackingEvent(this.promoType, this.featureOrder, this.featureIndex, 1, this.slides, this.featureLabels, this.masFeatureTrackingLabels);
            }
            e.element.setAttribute("data-tracked", String(true));
        }
    }

    trackSlideLargeShopPromo(e: CinemaChangeEvent) {
        const eventFeatureIndex = this.getFeatureIndex(e.element)
        if (e.element.className.includes("promo_shoppromo-large-card--container") && eventFeatureIndex == this.featureIndex) {
            const movementType: TrackingActionType = e.scrollingRight ? "next" : "previous"
            const slideContainers = Array.from(e.element.querySelectorAll('.pl_cinema__slide'));
            const slides = slideContainers.map((slideDom, i) => new Slide(slideDom, i))
            const visibleSlides = slides.slice(e.currentSlides.at(0), e.currentSlides.at(-1)! + 1)

            this.trackingService.sendLargeShoppromoSlideTrackingEvent(this.promoType, this.featureOrder, this.featureIndex, slides.length, visibleSlides, this.featureLabels, this.masFeatureTrackingLabels, movementType);
        }
    }

    trackClick(slide: Slide, filledSlots: number) {
        this.trackingService.sendLargeShopPromoClickTrackingEvent(this.promoType, this.featureOrder, this.featureIndex, filledSlots, slide, this.featureLabels, this.masFeatureTrackingLabels);
    }

    openAndTrackLeverLargeShoppromo(event: Event, slide: Slide, filledSlots: number) {
        // we have to open the sheet within the javascript code because the event.stopPropagation(), that is needed to prevent click tracking for the teaser,
        // prevents the pali layer open event.
        event.preventDefault();
        event.stopPropagation();
        const leverSheet = new window.o_global.pali.sheetBuilder({
            url: (event.target as HTMLElement).getAttribute("href"),
            lockMobileHeight: false
        });
        leverSheet.open();
        this.trackingService.sendLargeShopPromoLeverTrackingEvent(this.promoType, this.featureOrder, this.featureIndex, filledSlots, slide, this.featureLabels, this.masFeatureTrackingLabels);
    }

    private getPromoType(container: Element): PromoType {
        return container.getAttribute('data-promo-type')! as PromoType;
    }

    private getFeatureOrder(container: Element): number {
        const dataFeatureOrder = container.closest('[data-feature-order]')?.getAttribute('data-feature-order');
        return dataFeatureOrder ? parseInt(dataFeatureOrder, 0) : -1;
    }

    private getMasFeatureTrackingLabels(container: HTMLElement): TrackingFeatureLabels {
        const dataFeatureTrackingLabels = container.closest('[data-feature-tracking-labels]')?.getAttribute("data-feature-tracking-labels");
        return dataFeatureTrackingLabels ? JSON.parse(dataFeatureTrackingLabels) : {};
    }

    private getFeatureIndex(container: Element): number {
        return parseInt(container.getAttribute('data-feature-index')!, 0);
    }

    private getFeatureLabels(container: Element): TrackingFeatureLabels {
        return JSON.parse(container.getAttribute('data-feature-labels')!);
    }

    private isCinemaEnabled(container: Element): boolean {
        return container.querySelector(".pl_cinema__container") != null
    }
}